import { useRef, type RefObject } from "react"
import { toast } from "sonner"
import { IconLoader2 } from "@tabler/icons-react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { api } from "@/lib/api"
import { handledWorkspaceFailure } from "@/lib/benefit-save"
import { getBenefitProgramLabel } from "@/lib/benefit-enums"
import type { BenefitDetailDto, BenefitGrantDto, BenefitWorkflowResponse, ServiceProviderPaymentDto } from "@/lib/benefit-types"
import { captureGrantRemoval, grantRemovalCurrent, grantRemovalPath } from "@/lib/grant-removal"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"

/** The confirmation host stays independent of the current ledger row and retained-outcome modal. */
export function useGrantRemoval({ benefit, workflow, canEdit, onUpdated, section }: {
  benefit: BenefitDetailDto; workflow: BenefitWorkflowResponse | null; canEdit: boolean
  onUpdated(updated: BenefitDetailDto): void; section: RefObject<HTMLElement | null>
}) {
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "grant-removal")
  const [confirmation, setConfirmation] = useBenefitDraftState("grant-removal", "confirmation", null)
  const [saving, setSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const reviewingRef = useRef(reviewing); reviewingRef.current = reviewing
  const hasEditor = useHasOpenBenefitEditors()
  const origin = useRef<HTMLElement | null>(null)
  const focusLabel = useRef<string | null>(null)
  const completed = useRef(false)
  const latestId = useRef(benefit.id); latestId.current = benefit.id
  if (editing && confirmation) {
    const { program, year } = confirmation.context.grant.benefitProgramInstance
    focusLabel.current = `More actions for ${getBenefitProgramLabel(program)} PY ${year}`
  }
  const restoreFocus = () => {
    if (!section.current?.isConnected || reviewingRef.current) return
    const opener = origin.current
    const menu = [...section.current.querySelectorAll<HTMLButtonElement>("button[aria-label]")].find(button => button.getAttribute("aria-label") === focusLabel.current)
    const target = !completed.current && opener?.isConnected && opener.getClientRects().length && !opener.matches(":disabled") ? opener
      : !completed.current && menu && !menu.disabled ? menu : section.current.querySelector<HTMLElement>("#grants-workspace-heading")
    target?.focus()
  }
  const open = (grant: BenefitGrantDto, payment?: ServiceProviderPaymentDto, opener: HTMLElement | null = null) => {
    if (!canEdit || saving || reviewing || hasEditor) return
    const captured = captureGrantRemoval(benefit, grant, workflow, payment)
    if (!captured) return
    origin.current = opener ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
    completed.current = false
    setConfirmation(captured)
    setEditing(true)
  }
  const close = () => { if (!saving && !reviewing && setEditing(false)) setConfirmation(null) }
  const current = editing && canEdit && !reviewing && !!confirmation && token === confirmation.context.concurrencyToken && grantRemovalCurrent(confirmation, benefit, workflow)
  const remove = async () => {
    if (saving || !current || !confirmation) return
    setSaving(true)
    try {
      const updated = await api.del<BenefitDetailDto>(grantRemovalPath(confirmation), { concurrencyToken: token })
      if (latestId.current === confirmation.context.benefitId) onUpdated(updated)
      completed.current = true
      setEditing(false)
      restoreFocus()
      toast.success(confirmation.kind === "payment" ? "Vendor payment removed." : "Authored grant removed.")
    } catch (error) {
      if (handledWorkspaceFailure(confirmation.context.benefitId, error)) return
      toast.error(error instanceof Error ? error.message : "The removal was rejected.")
    } finally { setSaving(false) }
  }
  const name = confirmation?.kind === "payment" ? "vendor payment" : "authored grant"
  const instance = confirmation?.context.grant.benefitProgramInstance
  return { open, editing, dialog: <AlertDialog open={editing && !!confirmation && !reviewing} onOpenChange={value => { if (!value) close() }}>
    <AlertDialogContent onCloseAutoFocus={event => { event.preventDefault(); restoreFocus() }}>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
        <AlertDialogDescription>
          {instance && `${getBenefitProgramLabel(instance.program)} · PY ${instance.year}. `}
          {confirmation?.kind === "payment" ? `Remove the payment for vendor ${confirmation.vendorId}? No reversal payment will be created.` : "Remove this authored grant? This does not invalidate or replace a grant generation."}
        </AlertDialogDescription>
      </AlertDialogHeader>
      {!current && <p role="status" className="text-sm">This removal is not currently available. The original target and token are retained; cancel or refresh the saved data.</p>}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={saving} className="min-h-11 sm:min-h-9">Cancel</AlertDialogCancel>
        <AlertDialogAction disabled={saving || !current} variant="destructive" className="min-h-11 sm:min-h-9"
          onClick={event => { event.preventDefault(); void remove() }}>
          {saving && <IconLoader2 className="mr-1.5 size-4 animate-spin" />}Remove {confirmation?.kind === "payment" ? "payment" : "grant"}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog> }
}
